import type {
  IDaySchedule,
  IMatchScheduleItem,
  IScheduleConstraints,
  ITournamentSchedule,
} from '../../../../../types/schedule';
import type { Class, Court } from '../../../../../types/tournament';
import { isFreeSlot } from '../../../../../types/schedule';
import {
  getMatchDurationFromConstraints,
  getTournamentDayDisplayBounds,
} from '../schedule.helpers';
import {
  buildPlacementMatchRangesForCourt,
  PLACEMENT_KEYBOARD_MS_PER_MINUTE,
  PLACEMENT_KEYBOARD_SLOT_MINUTES,
  type PlacementKeyboardContext,
} from './schedulePlacementKeyboard';
import { getMatchEndMs } from './scheduleMatchEndTime';
import {
  filterValidPlacementStartsForConflicts,
  type PlacementConflictCheckParams,
  type PlacementConflictIndex,
} from '../../../../../features/schedule/schedulePlacementConflict';
import {
  getPlacementConflictIndex,
  placementConflictCacheKey,
} from '../../../../../features/schedule/placementConflictIndexCache';
import { getMatchScheduleInterval } from '../../../../../features/schedule/scheduleConflictDetector';
import type { IFreeSlotItem } from '../../../../../types/schedule';
import {
  computeValidPlacementBandsPx,
  computeValidPlacementStartTimesMs,
  type PlacementHighlightMatchRange,
  type SlotBandPx,
} from './scheduleGridPlacementHighlight';

export type PlacementCourtIndex = {
  courtId: number;
  court: Court;
  matchRanges: PlacementHighlightMatchRange[];
  validStartTimesMs: number[];
  bandsPx: SlotBandPx[];
};

export type PlacementDayIndex = {
  dayKey: number;
  dayStartMs: number;
  dayEndMs: number;
  courts: PlacementCourtIndex[];
};

export type PlacementSessionIndex = {
  days: PlacementDayIndex[];
};

type FilteredDayCacheKey = string;

const filteredDayCache = new Map<FilteredDayCacheKey, PlacementDayIndex>();
const MAX_FILTERED_DAY_CACHE = 24;

function filteredDayCacheKey(params: {
  schedule: ITournamentSchedule;
  pendingId: number;
  dayKey: number;
  blockOn: boolean;
  conflictDayKeys: number[];
  unscheduledMatches: IMatchScheduleItem[];
  classIdToObject: Record<string, Class>;
  scale: number;
  slotMinutes: number;
}): FilteredDayCacheKey {
  const scheduleKey = placementConflictCacheKey(
    params.schedule,
    params.conflictDayKeys,
    params.unscheduledMatches,
    params.classIdToObject
  );
  return `${scheduleKey}|${params.pendingId}|${params.dayKey}|${params.blockOn ? 1 : 0}|${params.scale}|${params.slotMinutes}`;
}

function rememberFilteredDay(key: FilteredDayCacheKey, day: PlacementDayIndex) {
  if (filteredDayCache.size >= MAX_FILTERED_DAY_CACHE) {
    const oldest = filteredDayCache.keys().next().value;
    if (oldest != null) filteredDayCache.delete(oldest);
  }
  filteredDayCache.set(key, day);
}

export type BuildPlacementSessionIndexParams = {
  days: IDaySchedule[];
  scheduleConstraints: IScheduleConstraints;
  activeMatch: IMatchScheduleItem;
  scale: number;
  timeZone: string;
  slotMinutes?: number;
  msPerMinute?: number;
  occupancySchedule?: ITournamentSchedule | null;
  blockInvalidPlacement?: boolean;
  placementConflictParams?: Omit<PlacementConflictCheckParams, 'courtId' | 'startMs'> | null;
};

function buildDayIndex(
  day: IDaySchedule,
  scheduleConstraints: IScheduleConstraints,
  activeMatch: IMatchScheduleItem,
  durationMins: number,
  scale: number,
  slotMinutes: number,
  msPerMinute: number,
  timeZone: string,
  occupancyDay?: IDaySchedule
): PlacementDayIndex {
  const dayConstraints = scheduleConstraints.days.find((d) => d.dayKey === day.dayKey);
  const scheduledOnDay = day.courts
    .flatMap((court) => court.matches)
    .filter((item): item is IMatchScheduleItem => !isFreeSlot(item) && !!item.scheduleDate);
  const fallbackStart = scheduledOnDay.length
    ? Math.min(...scheduledOnDay.map((item) => new Date(item.scheduleDate!).getTime()))
    : Date.now();
  const fallbackEnd = scheduledOnDay.length
    ? Math.max(
        ...scheduledOnDay.map((item) => {
          const startMs = new Date(item.scheduleDate!).getTime();
          return getMatchEndMs(item, startMs, Number.MAX_SAFE_INTEGER);
        })
      )
    : fallbackStart + 60 * msPerMinute;

  let dayStartMs = fallbackStart;
  let dayEndMs = fallbackEnd;
  if (dayConstraints) {
    const displayBounds = getTournamentDayDisplayBounds(
      dayConstraints,
      scheduledOnDay,
      scheduleConstraints,
      timeZone
    );
    dayStartMs = displayBounds.effectiveStartMs;
    dayEndMs = displayBounds.effectiveEndMs;
  }

  const courts: PlacementCourtIndex[] = day.courts.map((courtSchedule) => {
    const occupancyCourt = occupancyDay?.courts.find((c) => c.court.id === courtSchedule.court.id);
    const rangesSource = occupancyCourt ?? courtSchedule;
    const matchRanges = buildPlacementMatchRangesForCourt(rangesSource, dayEndMs);
    const validStartTimesMs = computeValidPlacementStartTimesMs({
      dayStartMs,
      dayEndMs,
      matchRanges,
      activeMatch,
      durationMins,
      slotMinutes,
      msPerMinute,
    });
    const bandsPx = computeValidPlacementBandsPx({
      dayStartMs,
      dayEndMs,
      matchRanges,
      activeMatch,
      durationMins,
      scale,
      slotMinutes,
      msPerMinute,
    });
    return {
      courtId: courtSchedule.court.id,
      court: courtSchedule.court,
      matchRanges,
      validStartTimesMs,
      bandsPx,
    };
  });

  return { dayKey: day.dayKey, dayStartMs, dayEndMs, courts };
}

function applyBlockFilterToCourt(
  court: PlacementCourtIndex,
  dayStartMs: number,
  dayEndMs: number,
  activeMatch: IMatchScheduleItem,
  durationMins: number,
  scale: number,
  slotMinutes: number,
  msPerMinute: number,
  placementConflictParams: Omit<PlacementConflictCheckParams, 'courtId' | 'startMs'>,
  placementConflictIndex: PlacementConflictIndex
): PlacementCourtIndex {
  const validStartTimesMs = filterValidPlacementStartsForConflicts(
    court.validStartTimesMs,
    court.courtId,
    placementConflictParams,
    placementConflictIndex
  );
  const bandsPx = computeValidPlacementBandsPx({
    dayStartMs,
    dayEndMs,
    matchRanges: court.matchRanges,
    activeMatch,
    durationMins,
    scale,
    slotMinutes,
    msPerMinute,
    validStartsMs: validStartTimesMs,
  });
  return { ...court, validStartTimesMs, bandsPx };
}

/** Occupancy-only session index (no conflict filter). */
export function buildPlacementSessionIndexBase({
  days,
  scheduleConstraints,
  activeMatch,
  scale,
  timeZone,
  slotMinutes = PLACEMENT_KEYBOARD_SLOT_MINUTES,
  msPerMinute = PLACEMENT_KEYBOARD_MS_PER_MINUTE,
  occupancySchedule,
}: Omit<
  BuildPlacementSessionIndexParams,
  'blockInvalidPlacement' | 'placementConflictParams'
>): PlacementSessionIndex {
  const durationMins =
    activeMatch.duration ||
    getMatchDurationFromConstraints(activeMatch, scheduleConstraints) ||
    30;
  return {
    days: days.map((day) => {
      const occupancyDay = occupancySchedule?.days.find((d) => d.dayKey === day.dayKey);
      return buildDayIndex(
        day,
        scheduleConstraints,
        activeMatch,
        durationMins,
        scale,
        slotMinutes,
        msPerMinute,
        timeZone,
        occupancyDay
      );
    }),
  };
}

/** Day keys to run conflict filter on; null = all days in the session index. */
export function resolveBlockFilterDayKeys(params: {
  sessionDayKeys: number[];
  singleGridDayKey?: number | null;
  focusedPlacementSlot?: IFreeSlotItem | null;
  activeMatch: IMatchScheduleItem;
  scheduleConstraints: IScheduleConstraints;
  timeZone: string;
  classIdToObject: Record<string, Class>;
}): number[] | null {
  const {
    sessionDayKeys,
    singleGridDayKey,
    focusedPlacementSlot,
    activeMatch,
    scheduleConstraints,
    timeZone,
    classIdToObject,
  } = params;
  if (singleGridDayKey != null) return [singleGridDayKey];
  if (focusedPlacementSlot?.day != null) return [focusedPlacementSlot.day];
  if (activeMatch.scheduleDate) {
    const classObject =
      classIdToObject[String(activeMatch.classId)] ?? classIdToObject[activeMatch.classId];
    const interval = getMatchScheduleInterval(
      activeMatch,
      scheduleConstraints,
      timeZone,
      classObject
    );
    if (interval) return [interval.dayKey];
  }
  if (sessionDayKeys.length <= 1) return sessionDayKeys;
  return [sessionDayKeys[0]];
}

export type BlockPlacementFilterResult = {
  sessionIndex: PlacementSessionIndex;
  conflictIndex: PlacementConflictIndex | null;
};

/** Apply block-invalid filter on top of a base index (cheap vs full rebuild). */
export function applyBlockPlacementFilter(
  sessionIndex: PlacementSessionIndex,
  params: {
    blockInvalidPlacement: boolean;
    placementConflictParams: Omit<PlacementConflictCheckParams, 'courtId' | 'startMs'> | null;
    scheduleConstraints: IScheduleConstraints;
    activeMatch: IMatchScheduleItem;
    scale: number;
    slotMinutes?: number;
    msPerMinute?: number;
    unscheduledMatches?: IMatchScheduleItem[];
    filterDayKeys?: number[] | null;
    mergeFrom?: PlacementSessionIndex | null;
  }
): BlockPlacementFilterResult {
  const {
    blockInvalidPlacement,
    placementConflictParams,
    scheduleConstraints,
    activeMatch,
    scale,
    slotMinutes = PLACEMENT_KEYBOARD_SLOT_MINUTES,
    msPerMinute = PLACEMENT_KEYBOARD_MS_PER_MINUTE,
    unscheduledMatches = [],
    filterDayKeys = null,
  } = params;

  if (
    !blockInvalidPlacement ||
    !placementConflictParams?.schedule ||
    !placementConflictParams.constraints
  ) {
    return { sessionIndex, conflictIndex: null };
  }

  const sessionDayKeys = sessionIndex.days.map((d) => d.dayKey);
  const conflictDayKeys =
    filterDayKeys != null && filterDayKeys.length > 0 ? filterDayKeys : sessionDayKeys;
  const placementConflictIndex = getPlacementConflictIndex(
    placementConflictParams.schedule,
    placementConflictParams.constraints,
    placementConflictParams.timeZone,
    conflictDayKeys,
    unscheduledMatches,
    placementConflictParams.classIdToObject
  );

  const durationMins =
    activeMatch.duration ||
    getMatchDurationFromConstraints(activeMatch, scheduleConstraints) ||
    30;

  const filterDayKeySet =
    filterDayKeys != null && filterDayKeys.length > 0 ? new Set(filterDayKeys) : null;
  const mergeFrom = params.mergeFrom;

  const days = sessionIndex.days.map((day) => {
    if (filterDayKeySet != null && !filterDayKeySet.has(day.dayKey)) {
      const merged = mergeFrom?.days.find((d) => d.dayKey === day.dayKey);
      return merged ?? day;
    }

    const cacheKey = filteredDayCacheKey({
      schedule: placementConflictParams.schedule!,
      pendingId: activeMatch.id,
      dayKey: day.dayKey,
      blockOn: true,
      conflictDayKeys,
      unscheduledMatches,
      classIdToObject: placementConflictParams.classIdToObject,
      scale,
      slotMinutes,
    });
    const cached = filteredDayCache.get(cacheKey);
    if (cached) return cached;

    const filteredDay: PlacementDayIndex = {
      ...day,
      courts: day.courts.map((court) =>
        applyBlockFilterToCourt(
          court,
          day.dayStartMs,
          day.dayEndMs,
          activeMatch,
          durationMins,
          scale,
          slotMinutes,
          msPerMinute,
          placementConflictParams,
          placementConflictIndex
        )
      ),
    };
    rememberFilteredDay(cacheKey, filteredDay);
    return filteredDay;
  });

  return {
    sessionIndex: { days },
    conflictIndex: placementConflictIndex,
  };
}

export function buildPlacementSessionIndex({
  days,
  scheduleConstraints,
  activeMatch,
  scale,
  timeZone,
  slotMinutes = PLACEMENT_KEYBOARD_SLOT_MINUTES,
  msPerMinute = PLACEMENT_KEYBOARD_MS_PER_MINUTE,
  occupancySchedule,
  blockInvalidPlacement = false,
  placementConflictParams = null,
}: BuildPlacementSessionIndexParams): PlacementSessionIndex {
  const base = buildPlacementSessionIndexBase({
    days,
    scheduleConstraints,
    activeMatch,
    scale,
    timeZone,
    slotMinutes,
    msPerMinute,
    occupancySchedule,
  });
  return applyBlockPlacementFilter(base, {
    blockInvalidPlacement,
    placementConflictParams,
    scheduleConstraints,
    activeMatch,
    scale,
    slotMinutes,
    msPerMinute,
  }).sessionIndex;
}

export function placementKeyboardContextFromSessionIndex(
  sessionIndex: PlacementSessionIndex,
  slotMinutes = PLACEMENT_KEYBOARD_SLOT_MINUTES,
  msPerMinute = PLACEMENT_KEYBOARD_MS_PER_MINUTE
): PlacementKeyboardContext {
  return {
    slotMinutes,
    msPerMinute,
    days: sessionIndex.days.map((day) => ({
      dayKey: day.dayKey,
      dayStartMs: day.dayStartMs,
      courts: day.courts.map((court) => ({
        court: court.court,
        validStartTimesMs: court.validStartTimesMs,
      })),
    })),
  };
}

export function getPlacementDayIndex(
  sessionIndex: PlacementSessionIndex | null,
  dayKey: number
): PlacementDayIndex | undefined {
  return sessionIndex?.days.find((d) => d.dayKey === dayKey);
}

/** Recompute placement band pixels only; valid start times and conflict results are unchanged. */
export function rescalePlacementSessionBandsPx(
  sessionIndex: PlacementSessionIndex,
  scale: number,
  scheduleConstraints: IScheduleConstraints,
  activeMatch: IMatchScheduleItem,
  slotMinutes = PLACEMENT_KEYBOARD_SLOT_MINUTES,
  msPerMinute = PLACEMENT_KEYBOARD_MS_PER_MINUTE
): PlacementSessionIndex {
  const durationMins =
    activeMatch.duration ||
    getMatchDurationFromConstraints(activeMatch, scheduleConstraints) ||
    30;

  return {
    days: sessionIndex.days.map((day) => ({
      ...day,
      courts: day.courts.map((court) => ({
        ...court,
        bandsPx: computeValidPlacementBandsPx({
          dayStartMs: day.dayStartMs,
          dayEndMs: day.dayEndMs,
          matchRanges: court.matchRanges,
          activeMatch,
          durationMins,
          scale,
          slotMinutes,
          msPerMinute,
          validStartsMs: court.validStartTimesMs,
        }),
      })),
    })),
  };
}
